import Dialog from '@/components/Dialog';
import Space from '@/components/Space';
import useApi from '@/lib/api/useApi';
import ModelType from '@/lib/constants/challengeModel/ModelType.enum';
import {
  type EditorModel,
  type EditorModelWithoutRelations,
} from '@/lib/types';
import { Button } from '@mui/material';
import { useContext, useState } from 'react';
import ModelsContext from '../../../ModelsContext';
import getDefaultModel from '../../../getDefaultModel';
import { FormattedMessage } from 'react-intl';
import FormattedMessageWithChallengeNaming from '@/components/FormattedMessageWithChallengeNaming';
import ModelDeactivatedWarning from './ModelDeactivatedWarning';

type ModelDefaultControlsProps = {
  model: EditorModel;
};

const ModelDefaultControls = ({ model }: ModelDefaultControlsProps) => {
  const api = useApi();

  const [, setModels] = useContext(ModelsContext);
  const [saving, setSaving] = useState(false);

  const makeModelDefault = async (): Promise<void> => {
    setSaving(true);

    const updatedModel = await api.post<EditorModelWithoutRelations>(
      `editor/challenge-model/models/${model.id}/make-default`,
    );

    setModels(models => {
      const previousDefaultModel = getDefaultModel(models, model.type);

      if (previousDefaultModel !== null) {
        models[previousDefaultModel.id].isDefault = false;
      }

      models[model.id] = {
        ...models[model.id],
        ...updatedModel,
      };
    });

    setSaving(false);
  };

  return (
    <div>
      <h3>
        <FormattedMessage id="editor.model_editor.model_selector.model_settings.dialog.default_controls.title" />
      </h3>
      <Dialog.ContentText>
        <FormattedMessageWithChallengeNaming
          id={`editor.model_editor.model_selector.model_settings.dialog.default_controls.text.${model.type === ModelType.CHALLENGE ? 'challenge' : 'task'}`}
          hasHtml
        />
      </Dialog.ContentText>
      <Space height={12} />
      <Button
        variant="contained"
        color="warning"
        onClick={makeModelDefault}
        disabled={model.deactivatedAt !== null}
        loading={saving}
        loadingPosition="start"
        disableElevation
      >
        <FormattedMessage id="editor.model_editor.model_selector.model_settings.dialog.default_controls.make_default_button" />
      </Button>
      {model.deactivatedAt !== null && <ModelDeactivatedWarning />}
    </div>
  );
};

export default ModelDefaultControls;
